import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import ValetApi from "../api/Api";

const LINE_COLORS = ["#2563eb", "#16a34a", "#dc2626", "#f59e0b", "#7c3aed", "#0891b2", "#be123c", "#0f766e", "#4f46e5", "#b45309"];
const HALF_HOUR_SLOTS_PER_DAY = 48;
const MOBILE_BREAKPOINT = 600;

function formatDateLabel(isoDate, includeWeekday = true) {
    const dateObj = new Date(`${isoDate}T12:00:00`);
    const formatOptions = includeWeekday ? { weekday: "short", month: "numeric", day: "numeric" } : { month: "numeric", day: "numeric" };
    try {
        return new Intl.DateTimeFormat("en-US", formatOptions).format(dateObj);
    } catch {
        return isoDate;
    }
}

function formatHalfHourLabel(slotOfDay) {
    const normalizedSlot = Number(slotOfDay);
    if (!Number.isInteger(normalizedSlot) || normalizedSlot < 0 || normalizedSlot >= HALF_HOUR_SLOTS_PER_DAY) {
        return String(slotOfDay || "");
    }

    const hour24 = Math.floor(normalizedSlot / 2);
    const minute = normalizedSlot % 2 === 0 ? "00" : "30";
    const hour12 = hour24 % 12 || 12;
    const meridiem = hour24 < 12 ? "AM" : "PM";
    return `${hour12}:${minute} ${meridiem}`;
}

function resolveHalfHourSlot(row) {
    const halfHourSlot = Number(row?.halfHourOfDay);
    if (Number.isInteger(halfHourSlot) && halfHourSlot >= 0 && halfHourSlot < HALF_HOUR_SLOTS_PER_DAY) {
        return halfHourSlot;
    }

    const hourOfDay = Number(row?.hourOfDay);
    if (Number.isInteger(hourOfDay) && hourOfDay >= 0 && hourOfDay < 24) {
        return hourOfDay * 2;
    }

    return null;
}

function formatHourOnlyLabel(label) {
    const text = String(label || "");
    const match = text.match(/^(\d{1,2})(?::\d{2})?\s*([AP]M)$/i);
    if (!match) return text;
    return `${Number(match[1])} ${match[2].toUpperCase()}`;
}

function buildMultiLineHourlyData(rows, selectedDates) {
    const normalizedDates = Array.isArray(selectedDates) ? selectedDates.filter(Boolean) : [];
    const dateSet = new Set(normalizedDates);
    const byHalfHour = new Map();

    for (let slot = 0; slot < HALF_HOUR_SLOTS_PER_DAY; slot += 1) {
        const hourLabel = formatHalfHourLabel(slot);
        const base = { halfHourOfDay: slot, hourLabel };
        normalizedDates.forEach((dateKey) => {
            base[`lot_${dateKey}`] = 0;
        });
        byHalfHour.set(slot, base);
    }

    rows.forEach((row) => {
        const localDate = String(row?.localDate || "");
        const halfHourSlot = resolveHalfHourSlot(row);
        if (!dateSet.has(localDate)) return;
        if (halfHourSlot === null) return;

        const target = byHalfHour.get(halfHourSlot);
        if (!target) return;

        target[`lot_${localDate}`] = Number(row?.totalInLot || 0);
    });

    return Array.from(byHalfHour.values()).sort((a, b) => a.halfHourOfDay - b.halfHourOfDay);
}

function buildSingleLineHourlyData(rows, selectedDates) {
    const normalizedDates = Array.isArray(selectedDates) ? selectedDates.filter(Boolean) : [];
    const dateSet = new Set(normalizedDates);
    const byHalfHour = new Map();

    for (let slot = 0; slot < HALF_HOUR_SLOTS_PER_DAY; slot += 1) {
        byHalfHour.set(slot, {
            halfHourOfDay: slot,
            hourLabel: formatHalfHourLabel(slot),
            total: 0,
            count: 0,
            totalInLot: 0,
        });
    }

    rows.forEach((row) => {
        const localDate = String(row?.localDate || "");
        const halfHourSlot = resolveHalfHourSlot(row);
        if (!dateSet.has(localDate)) return;
        if (halfHourSlot === null) return;

        const target = byHalfHour.get(halfHourSlot);
        if (!target) return;

        target.total += Number(row?.totalInLot || 0);
        target.count += 1;
    });

    return Array.from(byHalfHour.values())
        .sort((a, b) => a.halfHourOfDay - b.halfHourOfDay)
        .map((row) => ({
            halfHourOfDay: row.halfHourOfDay,
            hourOfDay: Math.floor(row.halfHourOfDay / 2),
            hourLabel: row.hourLabel,
            totalInLot: row.count > 0 ? Math.round(row.total / row.count) : 0,
        }));
}

function isValidStaffingTime(value) {
    return value !== "" && Number.isSafeInteger(Number(value)) && Number(value) > 0;
}

function estimateHourlyValets(totalIn, totalOut, averageSeconds) {
    if (totalIn + totalOut === 0) return 0;
    if (!isValidStaffingTime(averageSeconds)) return "--";

    const workSeconds = (totalIn + totalOut) * Number(averageSeconds);
    return Number.isFinite(workSeconds) ? Math.ceil(workSeconds / 3600) : "--";
}

function HourlyMovements({ rows, selectedDates, averageSeconds, onAverageSecondsChange }) {
    const hasInvalidTime = averageSeconds !== "" && !isValidStaffingTime(averageSeconds);

    return (
        <section className="dashboard-hourly-movements">
            <h3>Hourly Arrivals &amp; Departures</h3>
            <fieldset className="dashboard-staffing-inputs">
                <legend>Staffing Estimate: 60 Productive Minutes per Valet per Hour</legend>
                <label>
                    <span>Average time to park or retrieve (seconds)</span>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={averageSeconds}
                        aria-invalid={hasInvalidTime}
                        onChange={(event) => onAverageSecondsChange(event.target.value)}
                    />
                </label>
            </fieldset>
            {hasInvalidTime ? (
                <div
                    role="alert"
                    className="dashboard-error">
                    Average time must be positive whole seconds.
                </div>
            ) : null}
            {selectedDates.map((localDate) => {
                const hours = Array.from({ length: 24 }, (_, hour) => ({ hour, totalIn: 0, totalOut: 0 }));
                rows.forEach((row) => {
                    if (row.localDate !== localDate) return;
                    const slot = resolveHalfHourSlot(row);
                    if (slot === null) return;
                    const target = hours[Math.floor(slot / 2)];
                    target.totalIn += Number(row.totalIn || 0);
                    target.totalOut += Number(row.totalOut || 0);
                });
                const activeHours = hours.filter((row) => row.totalIn > 0 || row.totalOut > 0);
                const shiftHours = activeHours.length ? hours.slice(activeHours[0].hour, activeHours[activeHours.length - 1].hour + 1) : [];

                return (
                    <div
                        key={localDate}
                        className="dashboard-hourly-movements-day">
                        {shiftHours.length ? (
                            <table>
                                <caption>
                                    {formatDateLabel(localDate)} ({localDate})
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Hour</th>
                                        <th scope="col">Checked In</th>
                                        <th scope="col">Checked Out</th>
                                        <th scope="col">Total Movements</th>
                                        <th scope="col">Estimated Valets</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shiftHours.map(({ hour, totalIn, totalOut }) => (
                                        <tr key={hour}>
                                            <th scope="row">
                                                {formatHourOnlyLabel(formatHalfHourLabel(hour * 2))} - {formatHourOnlyLabel(formatHalfHourLabel(((hour + 1) % 24) * 2))}
                                            </th>
                                            <td>{totalIn}</td>
                                            <td>{totalOut}</td>
                                            <td>{totalIn + totalOut}</td>
                                            <td>{estimateHourlyValets(totalIn, totalOut, averageSeconds)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="dashboard-no-data">No arrivals or departures on {localDate}.</div>
                        )}
                    </div>
                );
            })}
        </section>
    );
}

const PodiumHourlyFlowChart = ({ podiumId, dateFilter, userTimeZone = "UTC" }) => {
    const [rawRows, setRawRows] = useState([]);
    const [averageSeconds, setAverageSeconds] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewportWidth, setViewportWidth] = useState(() => {
        if (typeof window === "undefined") return 1024;
        return window.innerWidth;
    });

    const selectedDates = useMemo(() => {
        const rows = Array.isArray(dateFilter?.dates) ? dateFilter.dates : [];
        if (!rows.length) return [dateFilter?.endDate || null].filter(Boolean);
        return rows;
    }, [dateFilter]);

    const multiLineMode = dateFilter?.preset === "same_weekday_4";
    const isMobileViewport = viewportWidth <= MOBILE_BREAKPOINT;
    const shouldCompressMobileTicks = isMobileViewport;

    const chartData = useMemo(() => {
        if (multiLineMode) {
            return buildMultiLineHourlyData(rawRows, selectedDates);
        }
        return buildSingleLineHourlyData(rawRows, selectedDates);
    }, [rawRows, selectedDates, multiLineMode]);

    const xTickStepHalfHours = useMemo(() => {
        if (!shouldCompressMobileTicks) return 1;

        const targetTickCount = viewportWidth <= 360 ? 6 : viewportWidth <= 430 ? 8 : 10;
        const rawStep = Math.ceil(HALF_HOUR_SLOTS_PER_DAY / targetTickCount);

        // Keep even step so ticks stay on whole-hour positions.
        return rawStep % 2 === 0 ? rawStep : rawStep + 1;
    }, [shouldCompressMobileTicks, viewportWidth]);

    const compressedMobileTicks = useMemo(() => {
        if (!shouldCompressMobileTicks) return undefined;
        return chartData.filter((row) => Number(row?.halfHourOfDay) % xTickStepHalfHours === 0).map((row) => row.hourLabel);
    }, [chartData, shouldCompressMobileTicks, xTickStepHalfHours]);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        function handleResize() {
            setViewportWidth(window.innerWidth);
        }

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!podiumId) return;

        let isMounted = true;

        async function loadHourlyFlow() {
            try {
                setLoading(true);
                setError(null);
                const res = await ValetApi.getPodiumHourlyFlowByPodiumDateRange(podiumId, userTimeZone, dateFilter?.startDate || null, dateFilter?.endDate || null);
                if (!isMounted) return;
                setRawRows(Array.isArray(res?.data?.hourlyFlow) ? res.data.hourlyFlow : []);
            } catch {
                if (!isMounted) return;
                setError("Unable to load hourly podium flow.");
                setRawRows([]);
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        loadHourlyFlow();

        return () => {
            isMounted = false;
        };
    }, [podiumId, dateFilter?.startDate, dateFilter?.endDate, userTimeZone]);

    const hasValues = useMemo(() => {
        if (multiLineMode) {
            return chartData.some((row) => selectedDates.some((dateKey) => Number(row?.[`lot_${dateKey}`] || 0) > 0));
        }
        return chartData.some((row) => Number(row?.totalInLot || 0) > 0);
    }, [chartData, selectedDates, multiLineMode]);

    const singleLineName = useMemo(() => {
        if (selectedDates.length <= 1) {
            return "Cars In Lot By 30-Min Interval";
        }
        return `Avg Cars In Lot By 30-Min Interval (${selectedDates.length} days)`;
    }, [selectedDates]);

    if (loading) {
        return <div className="dashboard-no-data">Loading hourly lot occupancy...</div>;
    }

    if (error) {
        return <div className="dashboard-no-data">{error}</div>;
    }

    const hourlyMovements = (
        <HourlyMovements
            rows={rawRows}
            selectedDates={selectedDates}
            averageSeconds={averageSeconds}
            onAverageSecondsChange={setAverageSeconds}
        />
    );

    if (!hasValues) {
        return (
            <>
                <div className="dashboard-no-data">No hourly lot occupancy data for this range.</div>
                {hourlyMovements}
            </>
        );
    }

    return (
        <div className="dashboard-hourly-chart-wrap">
            {!multiLineMode && selectedDates.length > 1 ? <div className="dashboard-no-data">Showing half-hour average occupancy across selected dates.</div> : null}
            <ResponsiveContainer
                width="100%"
                height={260}>
                <LineChart
                    data={chartData}
                    margin={{ top: 8, right: 10, left: 0, bottom: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="hourLabel"
                        interval={0}
                        ticks={compressedMobileTicks}
                        tickFormatter={shouldCompressMobileTicks ? formatHourOnlyLabel : undefined}
                        tickMargin={shouldCompressMobileTicks ? 8 : 8}
                        tick={{ fontSize: shouldCompressMobileTicks ? 9 : 10 }}
                        angle={shouldCompressMobileTicks ? 0 : -40}
                        textAnchor={shouldCompressMobileTicks ? "middle" : "end"}
                        height={shouldCompressMobileTicks ? 34 : 52}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    {multiLineMode ? <Legend /> : null}
                    {multiLineMode ? (
                        selectedDates.map((dateKey, idx) => {
                            const baseColor = LINE_COLORS[idx % LINE_COLORS.length];
                            const dateLabel = formatDateLabel(dateKey, true);

                            return (
                                <Line
                                    key={dateKey}
                                    type="monotone"
                                    dataKey={`lot_${dateKey}`}
                                    name={dateLabel}
                                    stroke={baseColor}
                                    strokeWidth={2.4}
                                    dot={false}
                                />
                            );
                        })
                    ) : (
                        <Line
                            type="monotone"
                            dataKey="totalInLot"
                            name={singleLineName}
                            stroke="#2563eb"
                            strokeWidth={2.7}
                            dot={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
            {hourlyMovements}
        </div>
    );
};

export default PodiumHourlyFlowChart;
